/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useInfiniteQuery, useQueryClient } from 'react-query'

import { getListProjects } from './projects.service'

import type { Project } from './project.types'

interface ProjectProps {
  pageSize?: number
  searchValue?: string
  enabled?: boolean
}

interface IInfiniteProjectsHook {
  isLoading: boolean
  isFetching: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  refreshProjects: () => void
  fetchNextPage: () => void
  projects: Project[]
  error?: unknown
  isError?: boolean
  pageSize: number
  pageCount: number
}

type ProjectsPage = {
  pageCount: number
  pageNumber: number
  pageSize: number
  entityList: Project[]
}

export const useInfiniteProjects: (
  props: ProjectProps,
) => IInfiniteProjectsHook = props => {
  const { pageSize = 20, searchValue = '', enabled = true } = props

  const queryClient = useQueryClient()

  const queryKey = ['projectsInfinite', { pageSize, searchValue }]

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<ProjectsPage>(
    queryKey,
    async ({ pageParam = 0 }) =>
      await getListProjects({
        pageNumber: pageParam,
        pageSize,
        searchValue,
      }),
    {
      getNextPageParam: (lastPage: ProjectsPage) => {
        const nextPageNumber = lastPage.pageNumber + 1

        if (nextPageNumber < lastPage.pageCount) {
          return nextPageNumber
        }

        return undefined
      },
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled,
    },
  )

  const projects =
    data?.pages?.flatMap((page: ProjectsPage) => page.entityList) ?? []

  const refreshProjects = () => queryClient.invalidateQueries(queryKey)

  return {
    projects,
    pageSize: data?.pages?.[0]?.pageSize ?? pageSize,
    pageCount: data?.pages?.[0]?.pageCount ?? 0,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    error,
    isError,
    refreshProjects,
    fetchNextPage,
  }
}
